// Pre-client UX hardening (Cortes): "Hacer corte" debe aterrizar en un formulario LISTO para
// trabajar, no en dos campos vacíos con el botón gris. Estas funciones puras proponen un rango
// razonable usando la lógica real del negocio (continuidad de cobertura: el turno de hoy empieza
// donde terminó el último corte, o al inicio del día de la TIENDA) y explican por qué un botón
// está deshabilitado (mismo patrón que el verificador del Ticket Mixto). Nada se guarda solo: la
// propuesta es visible y editable, y el corte solo se calcula/cierra cuando la persona lo pide.
import { dateToStoreISO, isoToStoreWallTime } from './timezones';

/** Fin ('endAt' ISO) del corte NO anulado más reciente, o null si no hay ninguno. */
export function latestCloseEndAt(closes: Array<{ endAt: string; status: string }>): string | null {
  let latest: string | null = null;
  for (const close of closes) {
    if (close.status === 'VOIDED') continue;
    if (latest === null || Date.parse(close.endAt) > Date.parse(latest)) latest = close.endAt;
  }
  return latest;
}

/**
 * Propone el rango del turno como horas de pared de la TIENDA ('YYYY-MM-DDTHH:mm'):
 * - fin = ahora (hora de la tienda), redondeado HACIA ARRIBA al minuto — ver nota;
 * - inicio = fin del último corte SI cayó dentro del día de hoy de la tienda (continuidad de
 *   cobertura — la misma regla por la que el preview avisa de tickets huérfanos);
 * - si no hay corte previo hoy, inicio = 00:00 del día de la tienda (neutro y seguro: cubre todo el
 *   día; nunca inventamos un horario de apertura del negocio).
 * El dispositivo NUNCA define "hoy": todo se calcula en la zona de la tienda.
 *
 * ZTV-09 (pre-client activation hardening): el input datetime-local solo tiene precisión de
 * minutos. Truncar "ahora" hacia abajo dejaba FUERA del rango cualquier venta hecha en el minuto en
 * curso (p. ej. venta a las 22:53:11 con fin propuesto 22:53:00 → diferencia de efectivo falsa en el
 * primer corte). "Hasta ahora" debe cubrir el momento actual completo, así que con segundos en el
 * reloj el fin se redondea al minuto SIGUIENTE — no es un colchón arbitrario: es el rango más chico
 * expresable en minutos que de verdad incluye "ahora". Aplica a ventas de cualquier canal.
 */
export function proposeShiftRange(
  lastCloseEndAt: string | null,
  now: Date,
  timeZone: string,
): { startAt: string; endAt: string } {
  const endInstant = now.getUTCSeconds() > 0 || now.getUTCMilliseconds() > 0
    ? new Date(Math.ceil(now.getTime() / 60_000) * 60_000)
    : now;
  const endAt = isoToStoreWallTime(endInstant.toISOString(), timeZone);
  const dayStart = `${dateToStoreISO(now, timeZone)}T00:00`;
  let startAt = dayStart;
  if (lastCloseEndAt) {
    const lastWall = isoToStoreWallTime(lastCloseEndAt, timeZone);
    // Solo continúa desde el último corte si terminó HOY (día de la tienda) y antes de "ahora";
    // un corte de hace días propondría un turno multi-día engañoso → mejor el inicio del día.
    if (lastWall >= dayStart && lastWall < endAt) startAt = lastWall;
  }
  return { startAt, endAt };
}

/**
 * Por qué "Calcular corte" está deshabilitado, en el idioma del usuario (null = habilitado).
 * FI-22 · Entrega 2: el bloque «Turno» del corte guiado se cierra completo (tienda, responsable y
 * horas), así que `employeeId` se exige cuando se pasa; sin él, la regla de siempre.
 */
export function calculateDisabledReason(input: { storeId: string; employeeId?: string; startAt: string; endAt: string }): string | null {
  if (!input.storeId) return 'Selecciona la tienda del corte.';
  if (input.employeeId === '') return 'Elige quién fue el responsable del turno.';
  if (!input.startAt || !input.endAt) return 'Captura el inicio y el fin del turno.';
  return null;
}

/** Respuesta a «¿Sacaste efectivo para depositar?»: nada preseleccionado hasta que la persona elige. */
export type DepositChoice = '' | 'yes' | 'no';

/** Un depósito «Sí» está completo cuando tiene cuenta y un monto mayor a cero. */
export function depositComplete(input: { depositAccount: string; depositAmount: string }): boolean {
  return input.depositAccount.trim() !== '' && input.depositAmount !== '' && Number(input.depositAmount) > 0;
}

/** Por qué "Guardar corte" está deshabilitado, en el idioma del usuario (null = habilitado). */
export function saveDisabledReason(input: {
  employeeId: string;
  cashOpen: string;
  countedCash: string;
  deposit: DepositChoice;
  depositAccount: string;
  depositAmount: string;
  requiredFormsComplete: boolean;
}): string | null {
  // Mismo orden que las preguntas en pantalla: caja inicial → depósito → efectivo contado.
  if (!input.employeeId) return 'Selecciona el empleado responsable del turno.';
  if (input.cashOpen === '') return 'Captura la caja inicial (puede ser 0).';
  if (input.deposit === '') return 'Indica si hubo depósito de efectivo.';
  if (input.deposit === 'yes' && !depositComplete(input)) return 'Completa la cuenta y el monto del depósito.';
  if (input.countedCash === '') return 'Cuenta el efectivo del cajón y captúralo.';
  if (!input.requiredFormsComplete) return 'Completa los formularios obligatorios del turno.';
  return null;
}
